/**
 * Frontend API client
 * Handles communication with backend API
 */

import type { ApiResponse, TeamMember, CapacityMetrics, Sprint, TimelineAllocation } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json() as Promise<T>;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Team endpoints
  async getTeam(page: number = 1, pageSize: number = 50): Promise<ApiResponse<any>> {
    return this.request(
      `/team?page=${page}&pageSize=${pageSize}`
    );
  }

  async getTeamMember(id: string): Promise<ApiResponse<TeamMember>> {
    return this.request(`/team/${id}`);
  }

  // Capacity endpoints
  async getCapacity(sprintId?: string, userId?: string): Promise<ApiResponse<CapacityMetrics>> {
    const params = new URLSearchParams();
    if (sprintId) params.append('sprint', sprintId);
    if (userId) params.append('user', userId);
    
    return this.request(`/capacity?${params.toString()}`);
  }

  // Allocations endpoints
  async getAllocations(
    startDate: Date,
    endDate: Date,
    userId?: string
  ): Promise<ApiResponse<TimelineAllocation[]>> {
    const params = new URLSearchParams();
    params.append('startDate', startDate.toISOString().split('T')[0]);
    params.append('endDate', endDate.toISOString().split('T')[0]);
    if (userId) params.append('user', userId);
    
    return this.request(`/allocations?${params.toString()}`);
  }

  // Sprint endpoints
  async getSprints(status?: string): Promise<ApiResponse<Sprint[]>> {
    const params = status ? `?status=${status}` : '';
    return this.request(`/sprints${params}`);
  }

  async getSprint(id: string): Promise<ApiResponse<Sprint>> {
    return this.request(`/sprints/${id}`);
  }

  // Configuration endpoints
  async getConfig(): Promise<ApiResponse<any>> {
    return this.request('/config');
  }

  async updateConfig(config: any): Promise<ApiResponse<any>> {
    return this.request('/config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  // Health check
  async health(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

export default ApiClient;
